import React, { memo, useContext, useCallback } from 'react';

import { SPACE_OVERVIEW, VIEW_PAGE } from '@confluence/named-routes';
import {
	Redirection,
	RoutesContext,
	useNavigationPolicy,
	DelayRedirectionOnClient,
} from '@confluence/route-manager';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';

type ContentRedirectComponentProps = {
	spaceKeyInUrl: string | undefined;
	contentSlugInUrl: string | undefined;
	contentWithSlug: any;
	space: any;
};

export const ContentRedirectComponent = memo(
	({
		contentWithSlug,
		space,
		spaceKeyInUrl,
		contentSlugInUrl = '',
	}: ContentRedirectComponentProps) => {
		const { match } = useContext(RoutesContext);
		const { routePolicy } = useNavigationPolicy();
		const isLivePage = useIsCurrentPageLive();

		const shouldRedirectIncorrectSpaceKey = useCallback(() => {
			if (isLivePage) {
				/*
				 * Live Pages exist on the view page route, but render the Confluence editor
				 * The Confluence editor has its own space key redirection logic and move event listeners (via useSpaceKeyWatcher)
				 * Consequently this redirection will clash with the redirects from the editor.
				 */
				return false;
			}
			return contentWithSlug && spaceKeyInUrl !== contentWithSlug?.spaceKey;
		}, [spaceKeyInUrl, contentWithSlug, isLivePage]);

		const shouldRedirectIncorrectSlug = useCallback(() => {
			// When current slug in URL does not match with slug in content ==> slug changed
			// Important to decode when compare because react router decodes the params first
			// so contentSlug will be decoded string
			//
			// Live pages will handle its own redirection logic from content slug
			return isLivePage
				? false
				: decodeURIComponent(contentWithSlug?.slug || '') !== decodeURIComponent(contentSlugInUrl);
		}, [contentWithSlug?.slug, contentSlugInUrl, isLivePage]);

		// Don't redirect if we have insufficient information to successfully redirect the user
		if (!contentWithSlug) {
			return null;
		}

		// Do redirect if we should go back to the Space Home Overview Tab
		if (space && contentWithSlug.id === `${space.homepageId}` && match?.name === VIEW_PAGE.name) {
			const willPushInSelf =
				!routePolicy ||
				routePolicy.willPushInSelf(
					VIEW_PAGE.name,
					match?.params.contentId,
					SPACE_OVERVIEW.name,
					contentWithSlug.id,
				);

			return willPushInSelf ? (
				<Redirection
					name={SPACE_OVERVIEW.name}
					params={{
						spaceKey: contentWithSlug.spaceKey,
						contentId: contentWithSlug.id,
						contentSlug: contentWithSlug.slug,
					}}
				/>
			) : null;
		}

		const isIncorrectSlug = shouldRedirectIncorrectSlug();
		const isIncorrectSpaceKey = shouldRedirectIncorrectSpaceKey();

		// Incorrect slug cannot do redirection in SSR, if there is #anchor fragment in the URL,
		// it will be lost in the redirection, as #anchor fragment is not passed to the server.
		// So we need to do redirection in client side.
		// If the content slug is already fixed in PageState, but URL is incorrect, rewrite URL without
		// triggering route change.
		if (
			!isIncorrectSpaceKey &&
			(isIncorrectSlug || window.__DELAYED_CONTENT_REDIRECTION__?.contentSlug) &&
			fg('confluence_skip_ssr_redirect_incorrect_slug')
		) {
			return (
				<DelayRedirectionOnClient
					name={match?.name}
					reason={`CRC-wrongSK:${isIncorrectSpaceKey}|wrongSlug:${isIncorrectSlug}`}
					params={{
						...match?.params,
						spaceKey: contentWithSlug.spaceKey,
						contentId: contentWithSlug.id,
						contentSlug: contentWithSlug.slug,
					}}
					skipRouteChange={Boolean(!isIncorrectSlug)} // Only skip route change if slug is correct
				/>
			);
		}

		// Don't redirect if we otherwise don't need to
		if (!isIncorrectSpaceKey && !isIncorrectSlug) {
			return null;
		}

		return (
			<Redirection
				name={match?.name}
				reason={`CRC-wrongSK:${isIncorrectSpaceKey}|wrongSlug:${isIncorrectSlug}`}
				params={{
					...match?.params,
					spaceKey: contentWithSlug.spaceKey,
					contentId: contentWithSlug.id,
					contentSlug: contentWithSlug.slug,
				}}
			/>
		);
	},
);
